import { type FormEvent, useId, useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { invariant } from "@searchui-searchforms/shared/utils";
import { useFormAPI, useFormStore } from "@/widgets/classic-form/form";
import { useOnOpenSearchAssist } from "@/widgets/classic-form/search-assist";
import type { SearchAssistModalAttributes } from "@/entities/form-metadata";
import { ValidatorProvider } from "@/shared/forms/tanstack";
import { Modal } from "@/shared/ui/ancestry";
import { Controls } from "./Controls";
import { BirthCalculator } from "./steps/BirthCalculator";
import { PossibleLocation } from "./steps/PossibleLocation";
import { Relatives } from "./steps/Relatives";
import styles from "./SearchAssistModal.module.scss";

type StepName = SearchAssistModalAttributes["steps"][number]["name"];

/**
 * Checks whether the user has filled in data for the current step.
 * Used to determine if the "next" button should show "Step N" (data entered)
 * or "I don't know" (no data entered).
 */
const useHasCurrentStepData = (stepName: StepName): boolean => {
    const location = useFormStore(({ values }) => values.events.Self?.instances?.[0]?.location);
    const birthYear = useFormStore(({ values }) => values.events.SelfBirth?.instances?.[0]?.date?.year);

    switch (stepName) {
        case "possibleLocation":
            return !!location?.trim();
        case "birthCalculator":
            return birthYear != null && birthYear !== 0;
        case "relatives":
            return false;
    }
};

export const SearchAssistModal = ({ steps }: SearchAssistModalAttributes) => {
    const { t } = useTranslation();
    const formId = useId();
    const formApi = useFormAPI();

    const [open, setIsOpen] = useState(false);
    const closeModal = () => setIsOpen(false);

    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [calculatorValues, setCalculatorValues] = useState({ age: "", year: "" });

    const givenName = useFormStore(({ values }) => values.name.givenName);
    useOnOpenSearchAssist(() => setIsOpen(true));

    const currentStepData = steps[currentStepIndex];
    invariant(currentStepData, "Current step index is out of bounds");

    const hasStepData = useHasCurrentStepData(currentStepData.name);

    const handleCalculatorValuesChange = (age: string, year: string) => {
        setCalculatorValues({ age, year });
    };

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (currentStepIndex + 1 === steps.length) {
            const formData = new FormData(e.currentTarget as HTMLFormElement);
            const dontShowSearchAssistAgain = formData.get("dontShowAgain") === "on";
            formApi.handleSubmit({ dontShowSearchAssistAgain });
        } else {
            setCurrentStepIndex((prevState) => prevState + 1);
        }
    };

    const onPrevStep = () => setCurrentStepIndex((prevState) => prevState - 1);

    return (
        <Modal open={open} onRequestClose={closeModal} closeOnBkgClick={false}>
            <div className="modalHeader">
                {t("searchAssist.improveResults", { step: currentStepIndex + 1, total: steps.length })}
            </div>
            {/*
             * The `open &&` guard prevents React warnings about nested <form> elements.
             * In runtime, forms are NOT actually nested because the modal content is
             * portaled to a separate DOM location outside the main form.
             */}
            {open && (
                <form id={formId} onSubmit={handleSubmit}>
                    <div className={classNames("modalSection", styles.assistModal)}>
                        {currentStepIndex === 0 && (
                            <p className={classNames("alert", styles.alert)} role="alert">
                                <strong>{t("searchAssist.searchTooBroad")}</strong>
                                {t("searchAssist.tellUsMore", { name: givenName })}
                            </p>
                        )}
                        {/*
                         * Modal content is portaled outside the main form's DOM tree.
                         * The ui.validator library uses scoped querySelector within its form element,
                         * so fields in portaled content won't be found by the parent form's validator.
                         *
                         * Solution: Create a separate form + ValidatorProvider for modal content.
                         * This gives modal fields their own isolated validator instance.
                         * React Context uses the nearest provider, so fields inside this modal
                         * will attach to this ValidatorProvider, not the parent one.
                         *
                         * Note: This creates nested ValidatorProviders in the React tree, which is
                         * suboptimal but unavoidable without changing the UI library's scoping behavior.
                         */}
                        <ValidatorProvider formId={formId}>
                            {currentStepData.name === "possibleLocation" && <PossibleLocation />}
                            {currentStepData.name === "birthCalculator" && (
                                <BirthCalculator
                                    calculatorAge={calculatorValues.age}
                                    calculatorYear={calculatorValues.year}
                                    onCalculatorValuesChange={handleCalculatorValuesChange}
                                />
                            )}
                            {currentStepData.name === "relatives" && <Relatives />}
                        </ValidatorProvider>
                    </div>
                    <Controls
                        currentStep={currentStepIndex + 1}
                        totalSteps={steps.length}
                        hasStepData={hasStepData}
                        onCancel={closeModal}
                        onPrevStep={onPrevStep}
                    />
                </form>
            )}
        </Modal>
    );
};
